/**
 * OAuth Helper Utility for Google Identity Services & Facebook SDK
 */

const GOOGLE_SCRIPT_URL = 'https://accounts.google.com/gsi/client';

let googleScriptLoaded = false;

/**
 * Detect if running inside a restricted WebView (Zalo, Facebook, Instagram, TikTok, etc.)
 * These environments block Google popup OAuth login.
 */
export function isRestrictedWebView() {
  const ua = navigator.userAgent || '';
  return (
    /FBAN|FBAV|FB_IAB|Instagram|TikTok|Zalo|ZaloApp|Musical\.ly|ByteDance/i.test(ua) ||
    // Generic Android WebView indicators
    (ua.includes('wv') && ua.includes('Android') && !ua.includes('Chrome/')) ||
    // iOS WebView (no Safari in UA but has AppleWebKit)
    (ua.includes('AppleWebKit') && !ua.includes('Safari') && ua.includes('Mobile'))
  );
}

/**
 * Build Google OAuth implicit flow URL (returns id_token directly via hash fragment)
 * Works in ANY browser / WebView because it's just a plain redirect, not a popup.
 *
 * @param {string} clientId - Google OAuth Client ID
 * @param {string} redirectUri - The URL Google will redirect back to (must be registered in Google Console)
 * @param {string} nonce - Random string to prevent replay attacks
 */
export function buildGoogleOAuthUrl(clientId, redirectUri, nonce) {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'id_token',
    scope: 'openid profile email',
    nonce: nonce,
    prompt: 'select_account'
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
}

/**
 * Trigger Google OAuth via full-page redirect (for restricted WebViews like Zalo).
 * Saves a nonce to sessionStorage, then redirects to Google login page.
 * Google will redirect back to `redirectUri` with #id_token=...
 *
 * @param {string} clientId - Google OAuth Client ID
 * @param {string} redirectUri - Must match what's registered in Google Cloud Console
 */
export function loginWithGoogleRedirect(clientId, redirectUri) {
  const nonce = Math.random().toString(36).substring(2) + Date.now().toString(36);
  sessionStorage.setItem('google_oauth_nonce', nonce);
  sessionStorage.setItem('google_oauth_redirect_origin', window.location.pathname);

  const url = buildGoogleOAuthUrl(clientId, redirectUri, nonce);
  window.location.href = url;
}

/**
 * Parse the Google OAuth callback from the URL hash fragment.
 * Call this on the page that is your redirect_uri.
 *
 * @returns {{ idToken: string, nonce: string } | null}
 */
export function parseGoogleOAuthCallback() {
  const hash = window.location.hash;
  if (!hash || !hash.includes('id_token')) return null;

  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const idToken = params.get('id_token');
  const storedNonce = sessionStorage.getItem('google_oauth_nonce');

  if (!idToken) return null;

  // Clean up
  sessionStorage.removeItem('google_oauth_nonce');

  // Remove the hash from URL without page reload
  try {
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
  } catch (_) {}

  return { idToken, nonce: storedNonce };
}

/**
 * Dynamically load an external script
 */
function loadScript(url, id) {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = url;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = (err) => reject(err);
    document.head.appendChild(script);
  });
}

/**
 * Load Google Identity Services (GIS) SDK
 */
export async function loadGoogleSDK() {
  if (googleScriptLoaded || window.google?.accounts?.id) {
    googleScriptLoaded = true;
    return window.google;
  }
  await loadScript(GOOGLE_SCRIPT_URL, 'google-jssdk');
  googleScriptLoaded = true;
  return window.google;
}



/**
 * Trigger Google OAuth Login flow and return idToken
 * - In restricted WebViews (Zalo, etc.): uses full-page redirect (implicit flow)
 * - On normal browsers: uses Google Identity Services SDK (popup/one-tap)
 *
 * @param {string} clientId Google OAuth Client ID
 * @param {string} [redirectUri] Required only for WebView redirect flow. Must be registered in Google Console.
 * @returns {Promise<string>} idToken (JWT token from Google)
 */
export function loginWithGoogle(clientId, redirectUri) {
  return new Promise(async (resolve, reject) => {
    // ── Path A: Capacitor native (Android/iOS app built with Capacitor) ──
    if (window.Capacitor?.isNativePlatform?.() && window.Capacitor?.Plugins?.GoogleAuth) {
      try {
        const googleAuthPlugin = window.Capacitor.Plugins.GoogleAuth;
        await googleAuthPlugin.initialize({
          clientId: clientId,
          scopes: ['profile', 'email'],
          grantOfflineAccess: true
        });
        const user = await googleAuthPlugin.signIn();
        if (user?.authentication?.idToken) {
          resolve(user.authentication.idToken);
          return;
        }
      } catch (nativeErr) {
        reject(new Error(nativeErr?.message || "Đã hủy đăng nhập Google trên ứng dụng."));
        return;
      }
    }

    // ── Path B: Restricted WebView (Zalo, Facebook, Instagram, TikTok...) ──
    // Google blocks popup OAuth in these environments.
    // Use full-page redirect (implicit flow) instead.
    if (isRestrictedWebView()) {
      if (!redirectUri) {
        // Default: redirect back to the current page's login route
        redirectUri = window.location.origin;
      }
      loginWithGoogleRedirect(clientId, redirectUri);
      // This function redirects the page, so the Promise never resolves here.
      // The result is handled in mounted() via parseGoogleOAuthCallback().
      return;
    }

    // ── Path C: Normal web browser — Google Identity Services SDK ──
    try {
      await loadGoogleSDK();

      if (!window.google?.accounts?.id) {
        reject(new Error("Google SDK chưa được nạp. Vui lòng thử lại."));
        return;
      }

      let resolved = false;

      const cleanup = () => {
        window.removeEventListener('focus', onFocus);
        if (focusTimer) clearTimeout(focusTimer);
        if (safetyTimer) clearTimeout(safetyTimer);
      };

      const doReject = (err) => {
        if (!resolved) {
          resolved = true;
          cleanup();
          reject(err);
        }
      };

      const doResolve = (val) => {
        if (!resolved) {
          resolved = true;
          cleanup();
          resolve(val);
        }
      };

      // Detect when window regains focus (user closed Google popup or canceled picker)
      let focusTimer = null;
      const onFocus = () => {
        if (!resolved) {
          focusTimer = setTimeout(() => {
            if (!resolved) {
              doReject(new Error("Đã hủy đăng nhập Google."));
            }
          }, 1500);
        }
      };
      window.addEventListener('focus', onFocus);

      // Safety timeout in case popup hangs
      const safetyTimer = setTimeout(() => {
        if (!resolved) {
          doReject(new Error("Đã hủy đăng nhập Google."));
        }
      }, 45000);

      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: (response) => {
          if (response && response.credential) {
            doResolve(response.credential);
          } else {
            doReject(new Error("Không lấy được Google Token. Vui lòng thử lại."));
          }
        },
        auto_select: false,
        use_fedcm_for_prompt: false
      });

      // Create a temporary hidden container to render and click Google official button
      const overlay = document.createElement('div');
      overlay.style.position = 'fixed';
      overlay.style.top = '-9999px';
      overlay.style.left = '-9999px';
      overlay.style.opacity = '0';
      document.body.appendChild(overlay);

      window.google.accounts.id.renderButton(overlay, {
        type: 'standard',
        size: 'large'
      });

      setTimeout(() => {
        const btn = overlay.querySelector('div[role="button"]');
        if (btn) {
          btn.click();
        } else {
          // Fallback to prompt
          window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment() || notification.isDismissedMoment()) {
              doReject(new Error("Đã hủy đăng nhập Google."));
            }
          });
        }

        // Clean up DOM element
        setTimeout(() => {
          if (document.body.contains(overlay)) {
            document.body.removeChild(overlay);
          }
        }, 5000);
      }, 50);

    } catch (err) {
      reject(err);
    }
  });
}
